import { X } from 'lucide-react';
import { EmptyState } from '../../../components/ui';
import { fmtPrice, cn } from '../../../lib/utils';
import EditableCell from './EditableCell';
import { RISK_UNIT_OPTIONS, unitShort } from './riskUnits';
import { qtyColumnLabel, qtyRowField } from './qtyModes';
import WatchlistMobileRows from './WatchlistMobileRows';

// Temporary UI simplification: only the protected "Limit" is selectable. Its stored/sent value stays
// 'Market' (backend MARKET → applies MPP/tick → sends a broker LIMIT) — no behavior change. The old true
// LIMIT and SL-M are hidden; SL-L is shown but disabled (coming soon).
export const ORDER_TYPE_OPTIONS = [
  { value: 'Market', label: 'Limit' },
  { value: 'SL-L', label: 'SL-L', disabled: true },
];

export const RISK_REFERENCE_OPTIONS = [
  { value: 'OPTION', label: 'OPTION' },
  { value: 'BASE', label: 'BASE' },
];

// BUY/SELL are disabled when the strategy is stopped or the row isn't actionable.
export function isRowLocked(strategyStatus, rowStatus) {
  return strategyStatus === 'STOPPED' || rowStatus === 'Pending' || rowStatus === 'Blocked';
}

// % change formatter for the additive WS `type:"quote"` payload — renders backend-provided
// change_percent verbatim (never derived/estimated from LTP locally). Missing → '—'.
export function fmtChangePercent(value) {
  const n = value == null ? NaN : Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${n > 0 ? '+' : ''}${n.toFixed(2)}%`;
}

// Sign-colored change_percent span — same bull/bear/muted convention as PnlCell
// (PositionsTable.jsx / TradeBookTable.jsx).
export function ChangePercentBadge({ quote, className }) {
  const n = quote?.change_percent == null ? NaN : Number(quote.change_percent);
  const cls = !Number.isFinite(n) ? 'text-txt-secondary' : n > 0 ? 'text-bull' : n < 0 ? 'text-bear' : 'text-txt-secondary';
  return <span className={cn('font-mono', cls, className)}>{fmtChangePercent(quote?.change_percent)}</span>;
}

function quoteDirectionClass(quote) {
  const n = quote?.change_percent == null ? NaN : Number(quote.change_percent);
  return !Number.isFinite(n) || n === 0 ? 'text-txt-secondary' : n > 0 ? 'text-bull' : 'text-bear';
}

function normSymbol(value) {
  return String(value || '').trim().toUpperCase();
}

function hasValidPrice(value) {
  const n = value == null || value === '' ? NaN : Number(value);
  return Number.isFinite(n) && n > 0;
}

function findOpenPositionForRow(row, openPositions) {
  if (!row) return null;
  if (row.backendId != null) {
    return openPositions.find((p) => p?.exitRowId === row.backendId) || null;
  }
  const rowSymbol = normSymbol(row.symbol);
  if (!rowSymbol) return null;
  return openPositions.find((p) => (
    [p?.sourceSymbol, p?.symbol, p?.positionSymbol, p?.resolvedOptionSymbol]
      .some((symbol) => normSymbol(symbol) === rowSymbol)
  )) || null;
}

function lastTradeForRow(row, openPositions) {
  const pos = findOpenPositionForRow(row, openPositions);
  const netQty = Number(pos?.netQty) || 0;
  if (!pos || netQty === 0) return null;

  const side = netQty > 0 ? 'BUY' : 'SELL';
  const avgPrice = side === 'BUY' ? pos.buyAvg : pos.sellAvg;
  const price = hasValidPrice(avgPrice) ? avgPrice : row.lastTriggerPrice;
  return { side, price: hasValidPrice(price) ? price : null };
}

// rows: mapped backend watchlist rows. onEditCell(row, field, value) → PATCH a single
// mutable field. onDelete(row) → DELETE the row.
export default function WatchlistTable({ strategy, rows, riskUnit, openPositions = [], onBuy, onSell, onEditCell, onDelete, onRowRiskUnitChange, pendingAddedSymbol, onPendingSymbolHandled }) {
  const qtyMode = strategy.manual_trading?.qty_mode;
  const qtyKey = qtyRowField(qtyMode);
  const showRiskReference = String(strategy.manual_trading?.trade_in || '').toUpperCase() === 'OPTION';

  const columns = [
    {
      key: 'displaySymbol',
      label: 'Symbol',
      width: '10rem',
      render: (v, row) => <span className="text-txt-primary font-semibold text-sm">{v || row.symbol}</span>,
    },
    {
      key: 'ltp',
      label: 'LTP',
      width: '5rem',
      render: (v, row) => (
        <span className={cn('font-semibold', quoteDirectionClass(row.quote))}>{fmtPrice(row.quote?.ltp ?? v)}</span>
      ),
    },
    {
      key: 'changePercent',
      label: 'Chg %',
      width: '4rem',
      render: (_v, row) => <ChangePercentBadge quote={row.quote} className="text-2xs font-semibold" />,
    },
    {
      key: qtyKey,
      label: qtyColumnLabel(qtyMode, strategy.manual_trading?.segment),
      width: '4.5rem',
      render: (v, row) => (
        <EditableCell
          value={v}
          width="3.75rem"
          placeholder=""
          onCommit={(val) => onEditCell(row, qtyKey, val)}
        />
      ),
    },
    {
      key: 'orderType',
      label: 'Order Type',
      width: '5.5rem',
      render: (v, row) => (
        <select
          value={String(v).toUpperCase() === 'SL-L' ? 'SL-L' : 'Market'}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onEditCell(row, 'orderType', e.target.value)}
          className="bg-bg-surface border border-bg-border rounded px-1.5 py-0.5 text-xs font-mono text-txt-primary focus:outline-none focus:border-amber/60"
        >
          {ORDER_TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>
          ))}
        </select>
      ),
    },
    {
      key: 'mpp',
      label: 'MPP',
      width: '3.75rem',
      render: (v, row) => (
        <EditableCell
          value={v}
          width="3.25rem"
          placeholder="0"
          onCommit={(val) => onEditCell(row, 'mpp', val)}
        />
      ),
    },
    ...(showRiskReference ? [{
      key: 'riskReferenceMode',
      label: 'Risk',
      width: '4.75rem',
      render: (v, row) => (
        <select
          value={String(v || row.riskReferenceMode || 'OPTION').toUpperCase() === 'BASE' ? 'BASE' : 'OPTION'}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onEditCell(row, 'riskReferenceMode', e.target.value)}
          title="Target/SL reference for dynamic option entries"
          className="bg-bg-surface border border-bg-border rounded px-1 py-0.5 text-2xs font-mono text-txt-secondary focus:outline-none focus:border-amber/60"
        >
          {RISK_REFERENCE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      ),
    }] : []),
    {
      key: 'riskUnit',
      label: 'Unit',
      width: '4.25rem',
      render: (_v, row) => (
        <select
          value={row.riskUnit || riskUnit}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onRowRiskUnitChange(row, e.target.value)}
          title="Risk unit for this row's Target/SL"
          className="bg-bg-surface border border-bg-border rounded px-1 py-0.5 text-2xs font-mono text-txt-secondary focus:outline-none focus:border-amber/60"
        >
          {RISK_UNIT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{unitShort(o.value)}</option>
          ))}
        </select>
      ),
    },
    {
      key: 'target',
      label: 'Target',
      width: '4rem',
      render: (v, row) => (
        <EditableCell value={v} width="3.75rem" placeholder="0" onCommit={(val) => onEditCell(row, 'target', val)} />
      ),
    },
    {
      key: 'stopLoss',
      label: 'SL',
      width: '4rem',
      render: (v, row) => (
        <EditableCell value={v} width="3.75rem" placeholder="0" onCommit={(val) => onEditCell(row, 'stopLoss', val)} />
      ),
    },
    {
      key: 'lastTrade',
      label: 'Last Trade',
      width: '7.25rem',
      render: (_v, row) => {
        const trade = lastTradeForRow(row, openPositions);
        if (!trade) return '—';
        const isBuy = trade.side === 'BUY';
        return (
          <span className={cn('inline-flex items-center gap-1 font-mono text-2xs font-semibold', isBuy ? 'text-bull' : 'text-bear')}>
            <span>{trade.side}</span>
            {trade.price != null && (
              <>
                <span className="text-txt-muted">@</span>
                <span>{fmtPrice(trade.price)}</span>
              </>
            )}
          </span>
        );
      },
    },
    {
      key: 'action',
      label: 'Action',
      width: '9rem',
      render: (_v, row) => {
        const locked = isRowLocked(strategy.status, row.status);
        return (
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                disabled={locked}
                onClick={() => onBuy(strategy, row)}
                className={cn(
                  'mt-action px-2.5 py-1 rounded-md text-2xs font-semibold border transition-colors',
                  locked
                    ? 'bg-bg-hover/40 border-bg-border text-txt-muted cursor-not-allowed opacity-50'
                    : 'bg-bull border-bull text-white hover:bg-bull/90 active:bg-bull/80',
                )}
              >
                BUY
              </button>
              <button
                type="button"
                disabled={locked}
                onClick={() => onSell(strategy, row)}
                className={cn(
                  'mt-action px-2.5 py-1 rounded-md text-2xs font-semibold border transition-colors',
                  locked
                    ? 'bg-bg-hover/40 border-bg-border text-txt-muted cursor-not-allowed opacity-50'
                    : 'bg-bear border-bear text-white hover:bg-bear/90 active:bg-bear/80',
                )}
              >
                SELL
              </button>
            </div>
            <span className="h-5 w-px bg-bg-border" aria-hidden="true" />
            <button
              type="button"
              title="Remove symbol"
              onClick={() => onDelete(row)}
              className="mt-delete p-1 rounded-md border border-bear bg-bear text-white hover:bg-bear/90 active:bg-bear/80 transition-colors"
            >
              <X size={13} />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      {/* Desktop — compact local table (not the shared ui/DataTable, which other Broker/Admin
          pages also use) so the sticky/shaded header and tightened row padding stay scoped to
          this Watchlist view only. Same `columns` (render functions, widths, labels) as before —
          only the surrounding table/header/row markup changed; row order is untouched (a
          Watchlist is not a chronological history). Bounded to ~6 data rows (a single
          overflow-auto container handles vertical + horizontal scroll together, so a 7th+ row
          scrolls internally without page-level horizontal overflow); the sticky, shaded header
          (bg-bg-hover — visibly distinct from the plain row background, opaque so scrolling row
          text never shows through) stays pinned during that scroll. Kept inside the existing
          `mt-table` wrapper so the light-theme `.mt-table td` tabular-nums rule still applies. */}
      <div className="mt-table hidden lg:block">
        {!rows.length ? (
          <EmptyState title="No symbols in this watchlist" />
        ) : (
          <div className="overflow-auto max-h-[17rem]">
            <table className="w-full text-xs">
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      className="sticky top-0 z-10 bg-bg-hover border-b border-bg-border text-left py-1.5 px-2.5 text-txt-label uppercase tracking-widest text-2xs font-semibold whitespace-nowrap"
                      style={{ width: col.width }}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={row.id ?? i} className="border-b border-bg-border/50">
                    {columns.map((col) => (
                      <td key={col.key} className="py-1.5 px-2.5 text-txt-secondary font-mono">
                        {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '—')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="lg:hidden">
        <WatchlistMobileRows
          strategy={strategy}
          rows={rows}
          riskUnit={riskUnit}
          openPositions={openPositions}
          onBuy={onBuy}
          onSell={onSell}
          onEditCell={onEditCell}
          onDelete={onDelete}
          onRowRiskUnitChange={onRowRiskUnitChange}
          pendingAddedSymbol={pendingAddedSymbol}
          onPendingSymbolHandled={onPendingSymbolHandled}
        />
      </div>
    </>
  );
}
